import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Alert, AlertDescription } from '~/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '~/components/ui/dialog';
import { CreditCard, Plus, Trash2, Loader2 } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { env } from '../../config/environment';
import { apiClient } from '../../api/client';
import { useWebSocketPaymentMethods } from '../../hooks/useWebSocketPaymentMethods';

const stripePromise = loadStripe(env.STRIPE_PUBLISHABLE_KEY);

interface PaymentMethod {
  id: string;
  type: 'card';
  card: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  created: number;
}

interface PaymentMethodsProps {
  // Props can be added here if needed for payment method selection
}

export function PaymentMethods(props: PaymentMethodsProps) {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [error, setError] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState<PaymentMethod | null>(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  // Stable callback for WebSocket change events to prevent listener recreation
  const handleWebSocketChange = useCallback(() => {
    console.log('[PaymentMethods] Received WebSocket change event, fetching fresh data');
    fetchPaymentMethods();
    setLastUpdate(new Date().toLocaleTimeString());
    // Clear the update notification after 3 seconds
    setTimeout(() => setLastUpdate(null), 3000);
  }, []);

  // Listen for WebSocket payment method change events
  const { isConnected } = useWebSocketPaymentMethods(handleWebSocketChange);

  // Log WebSocket connection status for debugging
  useEffect(() => {
    if (!isConnected) {
      console.warn('[PaymentMethods] WebSocket not connected - real-time updates will not work');
    } else {
      console.log('[PaymentMethods] WebSocket connected - real-time updates enabled');
    }
  }, [isConnected]);

  const fetchPaymentMethods = async () => {
    try {
      setIsLoading(true);
      const data = await apiClient.get<{ success: boolean; data?: { paymentMethods: PaymentMethod[] } }>(
        '/payments/payment-methods'
      );
      setPaymentMethods(data?.data?.paymentMethods || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch payment methods');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (method: PaymentMethod) => {
    setMethodToDelete(method);
    setShowDeleteDialog(true);
  };

  const confirmDeletePaymentMethod = async () => {
    if (!methodToDelete) return;

    try {
      setIsDeleting(true);
      setShowDeleteDialog(false);
      setError(''); // Clear any previous errors

      await apiClient.delete(`/payments/payment-methods/${methodToDelete.id}`);
      // Remove from local state
      setPaymentMethods(prev => prev.filter(pm => pm.id !== methodToDelete.id));
      setMethodToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete payment method');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setMethodToDelete(null);
  };

  const getBrandIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa': return '💳';
      case 'mastercard': return '💳';
      case 'amex': return '💳';
      default: return '💳';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Saved Payment Methods
        </CardTitle>
        <CardDescription>
          Save payment methods for quick purchases
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {lastUpdate && (
          <Alert>
            <AlertDescription>
              🔄 Payment methods updated automatically at {lastUpdate}
            </AlertDescription>
          </Alert>
        )}

        {paymentMethods.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No saved payment methods</p>
            <p className="text-sm">Add a payment method for quick purchases</p>
          </div>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getBrandIcon(method.card.brand)}</span>
                  <div>
                    <div className="font-medium capitalize">
                      {method.card.brand} •••• {method.card.last4}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Expires {method.card.exp_month.toString().padStart(2, '0')}/{method.card.exp_year}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteClick(method)}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={isAddingCard} onOpenChange={setIsAddingCard}>
          <DialogTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setIsAddingCard(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Payment Method
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md" aria-hidden="false">
            <DialogHeader>
              <DialogTitle>Add Payment Method</DialogTitle>
              <DialogDescription>
                Save a payment method for quick purchases
              </DialogDescription>
            </DialogHeader>
            <AddPaymentMethodForm
              onSuccess={() => {
                setIsAddingCard(false);
                fetchPaymentMethods();
                // Don't refresh immediately - rely entirely on WebSocket webhook
                // The webhook will trigger when Stripe has actually confirmed the payment method
                console.log('[PaymentMethods] Setup intent succeeded, waiting for Stripe webhook confirmation via WebSocket...');
              }}
              onError={setError}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-sm" aria-hidden="false">
            <DialogHeader>
              <DialogTitle>Delete Payment Method</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this payment method? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            {methodToDelete && (
              <div className="py-4">
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                  <span className="text-xl">{getBrandIcon(methodToDelete.card.brand)}</span>
                  <div>
                    <div className="font-medium capitalize">
                      {methodToDelete.card.brand} •••• {methodToDelete.card.last4}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Expires {methodToDelete.card.exp_month.toString().padStart(2, '0')}/{methodToDelete.card.exp_year}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeletePaymentMethod}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Yes, Delete
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Full screen spinner overlay */}
        {isDeleting && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin" />
              <div className="text-lg font-medium">Deleting payment method...</div>
              <div className="text-sm text-muted-foreground">Please wait while we remove this payment method.</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AddPaymentMethodForm({ 
  onSuccess, 
  onError 
}: { 
  onSuccess: () => void; 
  onError: (error: string) => void; 
}) {
  const [clientSecret, setClientSecret] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    createSetupIntent();
  }, []);

  const createSetupIntent = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.post<{ success: boolean; data: { clientSecret: string } }>('/payments/setup-intent');

      if (response.success && response.data?.clientSecret) {
        setClientSecret(response.data.clientSecret);
      } else {
        throw new Error('Invalid response format or missing client secret');
      }
    } catch (err: any) {
      console.error('[PaymentMethods] Setup intent error:', err);
      onError(err.message || 'Failed to create setup intent');
    } finally {
      setIsLoading(false);
    }
  };

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <Elements 
      stripe={stripePromise} 
      options={{ 
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#0f172a',
            colorBackground: '#ffffff',
            colorText: '#0f172a',
            colorDanger: '#dc2626',
            fontFamily: 'system-ui, sans-serif',
            spacingUnit: '4px',
            borderRadius: '6px',
          },
        },
      }}
    >
      <SetupForm onSuccess={onSuccess} onError={onError} />
    </Elements>
  );
}

function SetupForm({ onSuccess, onError }: { onSuccess: () => void; onError: (error: string) => void; }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      console.error('[SetupForm] Stripe or elements not available');
      return;
    }

    setIsProcessing(true);
    onError('');

    try {
      console.log('[SetupForm] Starting setup confirmation...');

      // First, check if the payment element is complete
      const paymentElement = elements.getElement('payment');
      if (!paymentElement) {
        throw new Error('Payment element not found');
      }

      // Submit the payment element to collect payment method data
      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.error('[SetupForm] Elements submit error:', submitError);
        throw new Error(submitError.message || 'Failed to submit payment details');
      }

      console.log('[SetupForm] Elements submitted successfully, confirming setup with params:', {
        return_url: `${window.location.origin}/credits`,
        redirect: 'if_required'
      });

      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/credits`,
        },
        redirect: 'if_required',
      });

      console.log('[SetupForm] Setup confirmation result:', { error, setupIntent });

      if (error) {
        console.error('[SetupForm] Setup confirmation error:', error);
        console.error('[SetupForm] Error details:', {
          type: error.type,
          code: error.code,
          decline_code: error.decline_code,
          message: error.message,
          payment_method: error.payment_method,
          setup_intent: error.setup_intent
        });
        throw new Error(error.message || 'Failed to save payment method');
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        console.log('[SetupForm] Setup intent succeeded:', setupIntent.id);
        onSuccess();
      } else {
        console.warn('[SetupForm] Setup intent not succeeded:', setupIntent?.status);
        throw new Error('Setup intent did not succeed');
      }

    } catch (err: any) {
      console.error('[SetupForm] Error during setup:', err);
      onError(err.message || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div 
        className="p-3 border rounded" 
        role="group" 
        aria-label="Payment method information"
        aria-hidden="false"
      >
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
          }}
          onReady={() => {
            console.log('[PaymentElement] Payment element is ready');
          }}
          onChange={(event) => {
            console.log('[PaymentElement] Payment element changed:', {
              complete: event.complete,
              empty: event.empty
            });
          }}
        />
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={!stripe || !elements || isProcessing}
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Plus className="mr-2 h-4 w-4" />
            Save Payment Method
          </>
        )}
      </Button>
    </form>
  );
}
